import { useCallback, useEffect, useRef, useState } from 'react';
import { useCodingScreen } from '../../interface/context';
import type { ReasonKind, ReasonOption } from '../../interface/reasons';

export type ReasonLoadState =
  | { readonly status: 'loading' }
  | { readonly status: 'loaded'; readonly options: readonly ReasonOption[] }
  | { readonly status: 'error' };

export function useReasonOptions(
  kind: ReasonKind,
  isOpen: boolean
): { state: ReasonLoadState; retry: () => void } {
  const { api } = useCodingScreen();
  const [state, setState] = useState<ReasonLoadState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const requestRef = useRef(0);

  useEffect(() => {
    if (!isOpen) return;
    const requestId = ++requestRef.current;
    setState({ status: 'loading' });
    const load = api?.loadReasonDictionary;
    if (typeof load !== 'function') {
      setState({ status: 'error' });
      return;
    }
    load(kind).then(
      (options) => {
        if (requestRef.current === requestId) setState({ status: 'loaded', options });
      },
      () => {
        if (requestRef.current === requestId) setState({ status: 'error' });
      }
    );
  }, [api, kind, isOpen, attempt]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  return { state, retry };
}
